import React, { useContext, useMemo } from 'react';
import { Formik, Form, Field, FormikErrors } from 'formik';
import { stripWhitespace } from 'src/helpers/formatting';
import { BlueTick } from 'src/assets/images';
import { RadioButton } from 'src/components';
import { Modal } from 'src/components/index';
import ShowIf from 'src/components/show-if/show-if';
import { isValidFileName } from 'src/helpers/validation';
import { Application, Catalogue, IdentityTypeDirectors, InputField } from 'src/features/common';
import { Button, ButtonParamType, DefaultButtonStyle } from '../button/revamp-button';
import { idMask } from 'src/helpers/formatting';
import { FlexWrap } from 'src/common/container/FlexWrap';
import {
  IDDocsUploaded,
  IdItem,
  OnboardingDocumentTypeEnum,
} from 'src/features/onboarding/types/upload-documents';
import { Environment } from 'src/Environment';
import useImageUpload from 'src/features/onboarding/hooks/use-image-upload';
import {
  getProductName,
  getProductNamesInCart,
} from 'src/features/dpo/helpers/tealium-service-helpers';
import { ImageUploadActionType } from 'src/features/onboarding/types/image-upload-reducer-actions';
import './doc-info-modal.scss';
import { ApplicationContext } from 'src/features/onboarding/context';

type Props = {
  show: boolean;
  fileNames: string[];
  ids?: IdItem[];
  isForeignNationalDocument: boolean;
  documentTypeName?: string;
  documentType: OnboardingDocumentTypeEnum;
  onHide: () => void;
  onClose?: (value: string[]) => void;
  onSubmission: (
    fileNames: string[],
    idNumber: string,
    idType: OnboardingDocumentTypeEnum,
    newFileName?: string,
  ) => void;
  isWebcamUpload?: boolean;
  idDocsUploaded: IDDocsUploaded[];
  disabled?: boolean;
  automationIdPrefix?: string;
  application: Application;
  catalogue: Catalogue;
};

type DocInfoFormErrors = {
  selectedId: string;
  documentName: string;
};

export default function DocInfoModal(props: Props) {
  const { application, catalogue } = props;
  const { onboardingTealiumService } = useContext(ApplicationContext);
  const { imageUploadState, imageUploadDispatch } = useImageUpload();

  const imageJoinEnabled = useMemo(
    (): boolean => !Environment.current.disabledFeatures.imageJoin,
    [],
  );

  const productNames = useMemo(() => {
    if (!Environment.current.disabledFeatures.dpo) {
      return getProductNamesInCart(application.cart || []);
    }
    return catalogue.products
      .filter(({ device }) => device.quantity > 0)
      .map(({ device }) => getProductName(device.title))
      .join(' | ');
  }, []);

  const getIdType = (docType: OnboardingDocumentTypeEnum) => {
    switch (docType) {
      case OnboardingDocumentTypeEnum.ID_CARD:
      case OnboardingDocumentTypeEnum.ID_BOOK:
      case 'ID':
        return 'an ID';

      case OnboardingDocumentTypeEnum.PASSPORT:
      case OnboardingDocumentTypeEnum.WORK_PERMIT:
      case OnboardingDocumentTypeEnum.PERMANENT_RESIDENCY:
        return 'a Passport';

      case OnboardingDocumentTypeEnum.ASYLUM_SEEKER:
        return 'an Asylum Seeker';

      case OnboardingDocumentTypeEnum.REFUGEE_LETTER:
        return 'a Refugee';

      default:
        return 'an Identity';
    }
  };

  function validateForm(values) {
    const errors: FormikErrors<DocInfoFormErrors> = {};

    if (props.ids && props.ids.length > 0) {
      const invalidId = !values.selectedId || values.selectedId === '';
      if (invalidId) {
        errors.selectedId = `Please select ${getIdType(values.idType)} number from the list`;
      }
    }

    if (props.isWebcamUpload && values.allowDocumentNameUpdate) {
      if (!values.documentName) {
        errors.documentName = 'Please enter a document name';
      } else if (!isValidFileName(values.documentName)) {
        errors.documentName = 'Please enter a valid document name';
      }
    }

    return errors;
  }

  function isFormValid(values) {
    return Object.keys(validateForm(values)).length === 0;
  }

  async function submitForm(values, { resetForm }) {
    if (values.selectedId?.idType === IdentityTypeDirectors.PASSPORT || values.isPassport) {
      values.idType = props.documentType;

      if (!props.isForeignNationalDocument) {
        values.idType = OnboardingDocumentTypeEnum.PASSPORT;
      }
    }

    resetForm({ selectedId: '', idType: OnboardingDocumentTypeEnum.ID_CARD });

    const documentName = values.documentName ? `${values.documentName}.jpeg` : undefined;

    props.onSubmission(
      props.fileNames,
      stripWhitespace(values.selectedId?.idNumber || values.selectedId),
      values.idType,
      documentName,
    );
    props.onHide();
  }

  let idList: IdItem[] = [];
  if (props.ids && props.ids?.filter((val) => val.idNumber !== undefined).length > 0) {
    idList = props.ids.map((id) => ({
      idType: id.idType,
      idNumber: id.idNumber.replace(/^(\d{6})(\d{4})(\d{3})$/, '$1 $2 $3'),
    }));
  }

  if (props.fileNames.find((fileName) => fileName.endsWith('.png'))) {
    idList = idList.filter((id) => id.idType !== null);
  }

  function getInitialValues() {
    const initialValues: { [key: string]: any } = {};

    if (props.isWebcamUpload && props?.fileNames?.length === 1) {
      const fileName = props.fileNames[0];
      initialValues.allowDocumentNameUpdate = true;
      initialValues.documentName = fileName === 'TBD' ? '' : fileName;
    } else {
      initialValues.allowDocumentNameUpdate = false;
    }

    if (props.ids && props.ids.length > 0) {
      initialValues.selectedId = props.ids[0];
      if (props.ids.length === 1) {
        initialValues.selectedId = props.ids[0].idNumber;
        initialValues.isPassport = props.ids[0].idType === IdentityTypeDirectors.PASSPORT;
      }

      initialValues.idType = props.isForeignNationalDocument
        ? props.documentType
        : OnboardingDocumentTypeEnum.ID_CARD;
    }
    return initialValues;
  }

  const disableSmartCard = (values, idDocsUploaded: IDDocsUploaded[]) => {
    if (idDocsUploaded && idDocsUploaded.length) {
      const idNumber = stripWhitespace(values.selectedId?.idNumber ?? values.selectedId);
      const idDocUploaded = idDocsUploaded.find((doc) => doc.idNumber === idNumber);

      if (idDocUploaded === undefined) return false;

      // Case 1: Green book is uploaded then disable Smart card
      const greenBookUploaded =
        idDocUploaded.numOfSmartCards === 0 && idDocUploaded.numOfGreenBooks > 0;

      // Case 2: Max number of smart cards is uploaded
      const maxReached = imageJoinEnabled
        ? idDocUploaded.numOfSmartCards === 2
        : idDocUploaded.numOfSmartCards === 1;

      return idDocUploaded && (greenBookUploaded || maxReached);
    }
    return false;
  };

  const disableGreenBook = (values, idDocsUploaded: IDDocsUploaded[]) => {
    // Case 1: \Two more files are dropped and imagejoin is enabled
    if (props.fileNames.length > 1 && imageJoinEnabled) {
      return true;
    }

    if (idDocsUploaded && idDocsUploaded.length) {
      const idNumber = stripWhitespace(values.selectedId?.idNumber ?? values.selectedId);
      const idDocUploaded = idDocsUploaded.find((doc) => doc.idNumber === idNumber);

      if (idDocUploaded === undefined) return false;

      // Case 2: Smart card is uploaded then disable Green book
      const smartCardUploaded = idDocUploaded.numOfSmartCards > 0;

      // Case 3: Max number of green books uploaded
      const maxReached = idDocUploaded.numOfGreenBooks === 1;

      return idDocUploaded && (smartCardUploaded || maxReached);
    }
    return false;
  };

  return (
    <section className="revamp-id-modal">
      <Formik
        initialValues={getInitialValues()}
        validate={validateForm}
        onSubmit={submitForm}
        validateOnChange={true}
        validateOnBlur={false}
        enableReinitialize={true}
      >
        {({ values, isSubmitting }) => (
          <Form>
            <Modal
              show={props.show}
              onShow={() => {
                if (props.isWebcamUpload) {
                  onboardingTealiumService.onDocumentDetailsModalLanding(
                    productNames,
                    application.referenceNumber,
                    imageUploadState.documentTypeName,
                    imageUploadState.uploadType,
                  );
                }
              }}
              cardStyle="ClassifyIdModal"
              headerStyle="has-text-centered"
              bodyStyle="modal-body has-text-centered has-padding-x-2 has-mobile-padding-x-6"
              footerStyle="has-text-centered"
              backdropStyle="backdrop"
              header={() => (
                <>
                  <img className="has-padding-top-4" src={BlueTick} />
                  <p className="successText">
                    {props.documentTypeName === 'SA ID / Passport'
                      ? 'ID Document'
                      : props.documentTypeName}{' '}
                    added
                  </p>
                  <p className="subtext">Image details</p>
                  <ShowIf condition={!values.allowDocumentNameUpdate}>
                    <div className="has-padding-top-6">
                      {props.fileNames.map((fileName) => {
                        return (
                          <p key={fileName} className="file-name-text has-padding-x-6">
                            {fileName}
                          </p>
                        );
                      })}
                    </div>
                  </ShowIf>
                </>
              )}
              body={() => (
                <>
                  <ShowIf condition={props.isWebcamUpload && values.allowDocumentNameUpdate}>
                    <p className="has-padding-y-6" id="basic-text">
                      Document details
                    </p>

                    <Field
                      name="documentName"
                      type="text"
                      placeholder="Enter document name"
                      component={InputField}
                      maxLength={255}
                      fieldType="standard"
                      className="light top-field"
                      automationid={`${props.automationIdPrefix}_Document_Name`}
                    />
                  </ShowIf>

                  <ShowIf condition={!!(props.ids && props.ids.length > 0)}>
                    {props.ids && props.ids?.length > 1 ? (
                      <Field
                        name="selectedId"
                        type="tel"
                        placeholder={`Select ${getIdType(values.idType)} number`}
                        items={idList}
                        getItemDisplayValue={(item: IdItem) => item.idNumber}
                        component={InputField}
                        fieldType="select"
                        className={'top-field bottom-field'}
                        automationid={`${props.automationIdPrefix}_Selected_ID`}
                      />
                    ) : (
                      <Field
                        name="selectedId"
                        type="tel"
                        placeholder="Selected ID number"
                        component={InputField}
                        fieldType="standard"
                        mask={idMask}
                        className={'top-field bottom-field'}
                        automationid={`${props.automationIdPrefix}_Selected_ID`}
                        disabled={true}
                      />
                    )}
                    {!props.isForeignNationalDocument && (
                      <FlexWrap gap="1rem">
                        <Field
                          type="radio"
                          name="idType"
                          value="New Identity Card"
                          label="ID Card"
                          component={RadioButton}
                          light={true}
                          automationId={`${props.automationIdPrefix}_ID_Card`}
                          disabled={disableSmartCard(values, props.idDocsUploaded)}
                        />
                        <Field
                          type="radio"
                          name="idType"
                          value="Old Green Identity Document"
                          label="ID Book"
                          component={RadioButton}
                          light={true}
                          automationId={`${props.automationIdPrefix}_Old_Green_ID_Document`}
                          disabled={disableGreenBook(values, props.idDocsUploaded)}
                        />
                      </FlexWrap>
                    )}
                  </ShowIf>
                </>
              )}
              footer={() => (
                <section className="button-container">
                  <Button
                    buttonStyle={DefaultButtonStyle()
                      .hasSecondaryAction()
                      .isMediumSize()
                      .hasLightMode()
                      .build()}
                    type="reset"
                    buttonParams={{
                      type: ButtonParamType.NormalButton,
                      onClick: () => {
                        if (!props.disabled) {
                          if (props.isWebcamUpload) {
                            onboardingTealiumService.onDocumentDetailsModalAction(
                              productNames,
                              application.referenceNumber,
                              imageUploadState.documentTypeName,
                              imageUploadState.uploadType,
                              'cancel',
                            );

                            imageUploadDispatch({
                              type: ImageUploadActionType.END_WEBCAM_JOURNEY,
                            });
                          }
                          props.onClose && props.onClose(props.fileNames);
                          props.onHide();
                        }
                      },
                    }}
                    className="long-button dark-text"
                    automationid={`${props.automationIdPrefix}_Cancel_Button`}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={isSubmitting || !isFormValid(values) || values.selectedId === '404'}
                    buttonStyle={DefaultButtonStyle().isMediumSize().hasPrimaryAction().build()}
                    type="submit"
                    buttonParams={{
                      type: ButtonParamType.NormalButton,
                      onClick: () => {
                        if (props.isWebcamUpload) {
                          onboardingTealiumService.onDocumentDetailsModalAction(
                            productNames,
                            application.referenceNumber,
                            imageUploadState.documentTypeName,
                            imageUploadState.uploadType,
                            'upload',
                          );

                          imageUploadDispatch({
                            type: ImageUploadActionType.END_WEBCAM_JOURNEY,
                          });
                        }
                      },
                    }}
                    className="long-button"
                    automationid={`${props.automationIdPrefix}_Upload_Button`}
                  >
                    Upload
                  </Button>
                </section>
              )}
              modalStyle={undefined}
              modalProps={undefined}
              modalWrapper={undefined}
              modalWrapperProps={undefined}
            />
          </Form>
        )}
      </Formik>
    </section>
  );
}
